import { organizationRestricted } from "@/core/auth/services/authActions";
import { selectCameraApiUrl } from "@/modules/project/services/projectSlice";
import type { RootState } from "@/store";
import { listenerMiddleware } from "@/store/listenerMiddleware";
import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { toast } from "sonner";
import { cameraApi } from ".";
import i18n from "@/core/i18n";

/** True if any camera API query ran, whatever its outcome. */
const hasCachedQueries = (state: RootState): boolean =>
  Object.keys(state[cameraApi.reducerPath]?.queries ?? {}).length > 0;

listenerMiddleware.startListening({
  predicate: (_action, currentState, previousState) => {
    const prev = selectCameraApiUrl(previousState as RootState);
    const curr = selectCameraApiUrl(currentState as RootState);

    if (prev === curr) {
      return false;
    }

    // Leaving a camera: always reset, whether for another camera or for no
    // camera at all. The cached data belongs to the camera we just left, and
    // keeping it around meant a relay session dropping or the local-access flag
    // being revoked left the previous camera's streams on screen.
    if (prev !== null) {
      return true;
    }

    // null -> URL is the normal startup transition, and resetting on it
    // unconditionally causes a spurious cameras-loading cycle that arms the
    // stream-recovery guard and forces an unnecessary WebRTC restart. But it is
    // only harmless while nothing has queried yet. If something did run before
    // the URL existed — a call site missing its skip gate — its entry is a
    // failure that nothing would ever retry, and the page waits forever on data
    // that will never arrive. Reset only in that case.
    return hasCachedQueries(currentState as RootState);
  },
  effect: (_action, api) => {
    api.dispatch(cameraApi.util.resetApiState());
  },
});

/**
 * The relay's answer when the organization's payment is overdue.
 *
 * Server-side enforcement lives in apps/relay-authz: it refuses any request
 * that would push a configuration or a model onto a camera. Reaching here means
 * the client's own gate did not fire — the flag arrives in the token payload,
 * so a session minted before the flag was flipped still believes it may deploy.
 */
const isOrgRestricted = (payload: unknown): boolean => {
  const error = payload as
    | (FetchBaseQueryError & { data?: { error?: string } })
    | undefined;

  return error?.status === 403 && error.data?.error === "ORG_RESTRICTED";
};

listenerMiddleware.startListening({
  predicate: (action) =>
    isRejectedWithValue(action) && isOrgRestricted(action.payload),
  effect: (_action, api) => {
    // Correct the stale flag first, so the banner appears and the Deploy button
    // disappears in the same tick as the toast.
    api.dispatch(organizationRestricted());
    toast.error(i18n.t("ORG_RESTRICTED", { ns: "errors" }));
  },
});
